/**
 * Phase + sub-stage label maps for the Accounts page.
 *
 * IMPORTANT: These maps are the **canonical-backbone defaults**, not the
 * source of truth for what the Accounts UI renders. The configurable Accounts
 * view (`@common/accountsView`, stored on `Client.features.accountsView` /
 * `InternalConfig.features.accountsView`) is the source of truth for stage and
 * sub-stage *labels, ordering, and column count*. Components resolve labels via
 * `useAccountsViewConfig()` + `resolveAccountPosition()` and only fall back to
 * these maps when no custom config exists.
 *
 * The label maps below re-export the canonical defaults from
 * `@common/accountsView` (`DEFAULT_PHASE_LABELS`, `DEFAULT_SUB_STAGE_LABELS`)
 * so the two can never drift. The grouping table (`PHASE_SUB_STAGES`) stays
 * local because it intentionally differs from the canonical grouping — it
 * collapses the review sub-stages into their approval milestones for the
 * lifecycle tracker. The rich `*_DESCRIPTION` / advance-requirement maps stay
 * here too (they're keyed off the canonical phase/sub-stage a custom stage
 * maps to) and remain UI-only.
 */

import type {LifecyclePhase, AccountsSubStage, AccountsArchiveReason} from '@api/generated/client';
import {
  DEFAULT_PHASE_LABELS,
  DEFAULT_SUB_STAGE_LABELS,
  findPositionBySystemSubStage,
  type AccountsViewConfig,
} from '@common/accountsView';

// `PROPOSAL` is intentionally omitted — proposals are now sub-stages of the
// Prospect (LEAD) phase, so there is no standalone Proposal column. The enum
// value still exists for backward-compat, hence the Record maps below keep its
// key, but it never appears in the ordered pipeline.
export const PHASE_ORDER: LifecyclePhase[] = ['LEAD', 'ONBOARDING', 'IMPLEMENTATION', 'ACTIVE'];

export const PHASE_LABEL: Record<LifecyclePhase, string> = DEFAULT_PHASE_LABELS;

// One-line description of what each lifecycle phase represents. Surfaced in the
// AccountDetail stage tooltips.
export const PHASE_DESCRIPTION: Record<LifecyclePhase, string> = {
  LEAD: 'A prospect being worked from first contact through proposal and presentation.',
  PROPOSAL: 'A proposal has been prepared and is being presented to the prospect.',
  ONBOARDING: 'The group is completing the RFC and the approvals needed to become a client.',
  IMPLEMENTATION: 'The approved group is being created and verified in CBS before going live.',
  ACTIVE: 'A live group with active coverage and ongoing servicing.',
};

// Ordered sub-stages within each phase. Drives the sub-stage progress bar in
// AccountDetail (how far along an account is inside its current phase). Order
// mirrors the canonical sequence in SUB_STAGE_LABEL / the API's SUB_STAGE_PHASE.
export const PHASE_SUB_STAGES: Record<LifecyclePhase, AccountsSubStage[]> = {
  // Proposal sub-stages now live in the Prospect (LEAD) phase. SWOT_CALL is
  // inserted between NEW_LEAD and the proposal steps for tenants that opt in.
  LEAD: ['NEW_LEAD', 'PROPOSAL_SENT', 'PROPOSAL_PRESENTATION'],
  // Vestigial — PROPOSAL is no longer an active phase; kept empty so the Record
  // stays total over the (backward-compatible) LifecyclePhase enum.
  PROPOSAL: [],
  // The onboarding tracker shows the meaningful milestones only: broker
  // approval, then client approval, then the MSA. The "review" sub-stages
  // (BROKER_REVIEW / CLIENT_REVIEW) are not separate steps — they collapse into
  // their approval milestone for display (see toDisplaySubStage).
  ONBOARDING: [
    'DRAFT',
    'SUBMITTED',
    'BROKER_APPROVED',
    'CLIENT_APPROVED',
    'MSA_PENDING',
    'MSA_SENT',
    'MSA_SIGNED',
  ],
  // 2026-07-08 reconciliation order: the Implementation Call follows Census
  // Approved for every tenant. WELCOME_CALL (Synrgy/Quantum only) is layered in
  // per tenant via the `welcomeCall` feature flag, mirroring
  // OPT_IN_SUB_STAGE_PHASE in @common/accountsView.
  IMPLEMENTATION: [
    'RFC_RECEIVED',
    'CENSUS_BUILD_OUT',
    'CENSUS_REVIEW',
    'CENSUS_UPDATE_NEEDED',
    'CENSUS_APPROVED',
    'IMPLEMENTATION_CALL',
    'DRY_RUN_1',
    'FINANCIAL_CALL',
    'DRY_RUN_2',
    'GO_LIVE_RUN',
  ],
  ACTIVE: ['ACTIVE'],
};

// Collapse the redundant RFC "review" sub-stages onto their approval milestone
// for any view driven by PHASE_SUB_STAGES (the lifecycle band, the pipeline
// card). An account "in broker review" and one "broker approved" occupy the
// same step on the tracker; the precise state is still shown by the sub-stage
// badge. Non-review sub-stages map to themselves.
const SUB_STAGE_DISPLAY_OVERRIDE: Partial<Record<AccountsSubStage, AccountsSubStage>> = {
  BROKER_REVIEW: 'BROKER_APPROVED',
  CLIENT_REVIEW: 'CLIENT_APPROVED',
};

export function toDisplaySubStage(subStage: AccountsSubStage): AccountsSubStage {
  return SUB_STAGE_DISPLAY_OVERRIDE[subStage] ?? subStage;
}

// Which milestone the account is *currently working toward* — the canonical
// sub-stage every Accounts-view surface displays (the lifecycle band's
// in-progress node, the kanban/table/header/sidebar badges, the stage tooltip),
// with every earlier node shown as completed. Unlike toDisplaySubStage (which
// collapses a review state onto its approval node), this advances a *completed*
// approval onto the next milestone so the pipeline reads as a checklist of work
// still to do:
//   - awaiting submission (DRAFT)                  → Draft
//   - submitted / in broker review                 → Broker Approval
//   - broker approved / in client review           → Client Approval
//   - client approved                              → MSA
// Every other sub-stage maps to itself.
const SUB_STAGE_ACTIVE_NODE: Partial<Record<AccountsSubStage, AccountsSubStage>> = {
  SUBMITTED: 'BROKER_APPROVED',
  BROKER_REVIEW: 'BROKER_APPROVED',
  BROKER_APPROVED: 'CLIENT_APPROVED',
  CLIENT_REVIEW: 'CLIENT_APPROVED',
  CLIENT_APPROVED: 'MSA_PENDING',
};

export function toActiveSubStage(subStage: AccountsSubStage): AccountsSubStage {
  return SUB_STAGE_ACTIVE_NODE[subStage] ?? subStage;
}

// Sub-stages representing deals awaiting CBS group-creation handoff (the "CBS
// queue"). Post-broker/client approval, pre-group-creation. Replaces the
// legacy READY_FOR_CBS_STATUSES (RfcStatus.CLIENT_APPROVED / MSA_SIGNED).
export const READY_FOR_CBS_SUB_STAGES: AccountsSubStage[] = ['CLIENT_APPROVED', 'MSA_SIGNED'];

export const SUB_STAGE_LABEL: Record<AccountsSubStage, string> = DEFAULT_SUB_STAGE_LABELS;

// Badge variant per sub-stage. Kept in parity with the prospect pipeline
// (getProspectStageColor) and the deal/RFC pipeline (RFC getStatusColor) so an
// account's status reads the same color everywhere. Only legible variants are
// used (info/warning/secondary/success) — never the default blue-on-black/50
// badge, which has poor contrast.
export type SubStageBadgeVariant = 'info' | 'warning' | 'secondary' | 'success' | 'teal' | 'green';

export const SUB_STAGE_BADGE_VARIANT: Record<AccountsSubStage, SubStageBadgeVariant> = {
  // Lead — parity with prospect pipeline
  NEW_LEAD: 'info',
  // SWOT call — in-progress amber while requested/scheduled
  SWOT_CALL: 'warning',
  // Proposal — parity with prospect pipeline
  PROPOSAL_SENT: 'secondary',
  PROPOSAL_PRESENTATION: 'warning',
  // Onboarding — parity with RFC status pipeline
  DRAFT: 'secondary',
  SUBMITTED: 'warning',
  BROKER_REVIEW: 'warning',
  // Broker + client approval share the dedicated `green` hook class (no other
  // sub-stage uses it). Renders the same emerald as `success` in the default
  // theme, but lets a portal recolor *only* the two approval steps — Synrgy
  // overrides `.cb-badge--green` to amber to match its "Broker Review" banner.
  BROKER_APPROVED: 'green',
  CLIENT_REVIEW: 'info',
  CLIENT_APPROVED: 'green',
  // Proposal step (post-approval, Synrgy) — amber while the proposal is worked.
  PROPOSAL: 'warning',
  MSA_PENDING: 'warning',
  MSA_SENT: 'info',
  MSA_SIGNED: 'success',
  // Submitted to CBS, awaiting implementation kickoff — same handoff blue as
  // RFC Received, the implementation step it advances into.
  SUBMITTED_FOR_IMPLEMENTATION: 'info',
  // Implementation — CBS handoff, parity with the RFC CBS processing pipeline
  RFC_RECEIVED: 'info',
  WELCOME_CALL: 'info',
  CENSUS_BUILD_OUT: 'info',
  CENSUS_REVIEW: 'warning',
  CENSUS_UPDATE_NEEDED: 'warning',
  // Census sign-off milestone — same green as the approval steps.
  CENSUS_APPROVED: 'success',
  IMPLEMENTATION_CALL: 'info',
  DRY_RUN_1: 'info',
  FINANCIAL_CALL: 'info',
  DRY_RUN_2: 'info',
  GO_LIVE_RUN: 'success',
  // Active — flat teal-green, distinct from the emerald approval stages.
  ACTIVE: 'teal',
};

// Banner / status-pill hex colors per displayed sub-stage (used by surfaces
// that render raw colors rather than Badge variants, e.g. the RFC tab's status
// banner). Pending work reads amber/blue, achieved milestones read green, CBS
// handoff reads indigo. Keep the tone-per-stage story aligned with
// SUB_STAGE_BADGE_VARIANT above.
const AMBER = {bg: '#FEF3C7', text: '#D97706'};
const BLUE = {bg: '#DBEAFE', text: '#1D4ED8'};
const GREEN = {bg: '#D1FAE5', text: '#059669'};
const PURPLE = {bg: '#EDE9FE', text: '#6B21A8'};
const INDIGO = {bg: '#E0E7FF', text: '#1E3A5F'};

export const SUB_STAGE_STATUS_COLORS: Record<AccountsSubStage, {bg: string; text: string}> = {
  NEW_LEAD: BLUE,
  SWOT_CALL: AMBER,
  PROPOSAL_SENT: BLUE,
  PROPOSAL_PRESENTATION: AMBER,
  DRAFT: PURPLE,
  SUBMITTED: BLUE,
  BROKER_REVIEW: AMBER,
  BROKER_APPROVED: AMBER,
  CLIENT_REVIEW: AMBER,
  CLIENT_APPROVED: AMBER,
  // Post-approval Proposal step (Synrgy) — amber while the proposal is worked.
  PROPOSAL: AMBER,
  MSA_PENDING: AMBER,
  MSA_SENT: BLUE,
  MSA_SIGNED: GREEN,
  // Submitted to CBS, awaiting implementation kickoff — same handoff indigo as
  // RFC Received, the implementation step it advances into.
  SUBMITTED_FOR_IMPLEMENTATION: INDIGO,
  RFC_RECEIVED: INDIGO,
  WELCOME_CALL: INDIGO,
  CENSUS_BUILD_OUT: INDIGO,
  CENSUS_REVIEW: INDIGO,
  CENSUS_UPDATE_NEEDED: INDIGO,
  CENSUS_APPROVED: GREEN,
  IMPLEMENTATION_CALL: INDIGO,
  DRY_RUN_1: INDIGO,
  FINANCIAL_CALL: INDIGO,
  DRY_RUN_2: INDIGO,
  GO_LIVE_RUN: GREEN,
  ACTIVE: GREEN,
};

// One-line description of what each sub-stage means. Surfaced in the
// AccountDetail stage tooltips next to the current stage. Keyed by the
// *displayed* sub-stage (toActiveSubStage), so the approval milestones are
// described as pending — they are only ever displayed while the approval is
// still being worked toward (once granted, the display advances past them).
export const SUB_STAGE_DESCRIPTION: Record<AccountsSubStage, string> = {
  NEW_LEAD: 'A prospect in early conversation.',
  SWOT_CALL: 'A multi-stakeholder SWOT call is being arranged before the proposal.',
  PROPOSAL_SENT: 'A proposal has been sent to the prospect for review.',
  PROPOSAL_PRESENTATION: 'Walking the prospect through the proposal in a presentation.',
  DRAFT: 'The RFC is being drafted and has not been submitted yet.',
  SUBMITTED: 'The RFC has been submitted and is awaiting review.',
  BROKER_REVIEW: 'The broker is reviewing the RFC details.',
  BROKER_APPROVED: 'The submitted RFC is awaiting the broker’s approval.',
  CLIENT_REVIEW: 'The client is reviewing the submitted RFC.',
  CLIENT_APPROVED: 'The broker has approved the RFC; awaiting the client’s approval.',
  PROPOSAL: 'Generate the proposal to present to the approved group.',
  MSA_PENDING: 'Awaiting the Master Service Agreement to be prepared.',
  MSA_SENT: 'The MSA has been sent out for signature.',
  MSA_SIGNED: 'The MSA has been signed by all parties.',
  SUBMITTED_FOR_IMPLEMENTATION:
    'The group has been submitted to CBS and is awaiting implementation to begin.',
  RFC_RECEIVED: 'The required RFC ingredients are received and confirmed by CBS.',
  WELCOME_CALL:
    'Welcoming the group — introductions and a walkthrough of the implementation ahead.',
  CENSUS_BUILD_OUT: 'Building the census or the API integration for the group.',
  CENSUS_REVIEW: 'The extraction team is reviewing the census and relaying any needed changes.',
  CENSUS_UPDATE_NEEDED:
    'Census edits are in progress with the group; toggles with Census Review until approved.',
  CENSUS_APPROVED:
    'The census has been reviewed and signed off; ready for the Implementation Call.',
  IMPLEMENTATION_CALL:
    'Confirming group data — pay frequency, payroll vendor, processing days, and census build-out type.',
  DRY_RUN_1:
    'Generating the current census and supporting docs and uploading them to the FTP site.',
  FINANCIAL_CALL: 'Walking the group through the money flow and how to read the Statement.',
  DRY_RUN_2: 'Testing the import file in an open payroll and backing it out once verified.',
  GO_LIVE_RUN: 'Applying the import file and processing payroll — the group is going live.',
  ACTIVE: 'The group is live with active coverage.',
};

// The single next action that moves the account out of each sub-stage. Surfaced
// in the Accounts table "Next Action" column and the AccountDetail stage tooltip
// as the "To advance" line. Prospect/Onboarding/Active labels mirror the Synrgy
// portal's NEXT_ACTION_MAP so the wording matches across products; the
// Implementation sub-stages keep the CBS-specific copy below. Every entry is
// kept to 4 words or fewer so the "Next Action" column never truncates.
export const SUB_STAGE_ADVANCE_REQUIREMENT: Record<AccountsSubStage, string> = {
  NEW_LEAD: 'Set up SWOT call',
  SWOT_CALL: 'Generate a proposal',
  PROPOSAL_SENT: 'Generate informed proposal',
  PROPOSAL_PRESENTATION: 'Generate RFC',
  DRAFT: 'Send RFC to client',
  SUBMITTED: 'Review & approve RFC',
  BROKER_REVIEW: 'Review & approve RFC',
  BROKER_APPROVED: 'Review & approve RFC',
  CLIENT_REVIEW: 'Review & approve RFC',
  CLIENT_APPROVED: 'Send MSA Package',
  PROPOSAL: 'Generate proposal',
  MSA_PENDING: 'Send MSA Package',
  MSA_SENT: 'Awaiting client signature',
  MSA_SIGNED: 'Send to CBS',
  SUBMITTED_FOR_IMPLEMENTATION: 'Awaiting CBS processing',
  // The step after RFC Received is Census / API Build Out — except for
  // welcome-call tenants (Synrgy/Quantum), handled in subStageAdvanceRequirement.
  RFC_RECEIVED: 'Begin census build-out',
  WELCOME_CALL: 'Begin census build-out',
  CENSUS_BUILD_OUT: 'Send census for review',
  CENSUS_REVIEW: 'Approve or request updates',
  CENSUS_UPDATE_NEEDED: 'Re-submit updated census',
  CENSUS_APPROVED: 'Hold implementation call',
  IMPLEMENTATION_CALL: 'Run first dry run',
  DRY_RUN_1: 'Hold financial call',
  FINANCIAL_CALL: 'Run second dry run',
  DRY_RUN_2: 'Apply import, go live',
  GO_LIVE_RUN: 'Create the group',
  ACTIVE: 'Monitor engagement metrics',
};

/**
 * Config-aware advance requirement. The NEW_LEAD requirement names the SWOT
 * Call stage, so interpolate the tenant's configured label for the SWOT_CALL
 * sub-stage (renameable in the accounts-view editor, e.g. Synrgy's "SWAT
 * Call") — same resolution the detail view's SwotControls uses. RFC_RECEIVED
 * points at the Welcome Call for welcome-call tenants (mirrors
 * NextActionButton) when the caller can supply the per-account flag. Every
 * other sub-stage falls through to the static map.
 */
export function subStageAdvanceRequirement(
  subStage: AccountsSubStage,
  config: AccountsViewConfig,
  opts?: {welcomeCallEnabled?: boolean},
): string {
  if (subStage === 'NEW_LEAD') {
    const swotLabel = findPositionBySystemSubStage(config, 'SWOT_CALL')?.subStage.label;
    if (swotLabel) return `Set up ${swotLabel}`;
  }
  if (subStage === 'RFC_RECEIVED' && opts?.welcomeCallEnabled) {
    const welcomeLabel =
      findPositionBySystemSubStage(config, 'WELCOME_CALL')?.subStage.label ??
      DEFAULT_SUB_STAGE_LABELS.WELCOME_CALL;
    return `Hold ${welcomeLabel.toLowerCase()}`;
  }
  return SUB_STAGE_ADVANCE_REQUIREMENT[subStage];
}

// Why an account left the pipeline. ON_HOLD and REJECTED are the user-facing
// On Hold / Lost statuses (badges, status filters, CSV export); the other two
// are legacy reasons from the retired generic-archive flow, kept so old rows
// still render a meaningful badge.
export const ARCHIVE_REASON_LABEL: Record<AccountsArchiveReason, string> = {
  REJECTED: 'Lost',
  ON_HOLD: 'On Hold',
  INACTIVE_GROUP: 'Group Inactive',
  ARCHIVED_MANUAL: 'Manually Archived',
};

export function isOverdue(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.getTime() < Date.now();
}

export function relativeTime(date: Date | string | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}
